<template>
  <div v-if="job">

    <div class="flex flex-wrap -mx-2">
      <div class="w-full lg:w-1/3 xl:w-1/4 px-2">

        <div class="box">
          <div class="flex justify-between items-end">
            <p class="text-lg text-theme-darker light:text-theme-lighter">Job <i>#{{ job.id }}</i></p>
            <p class="leading-none text-right">
              <span class="block font-bold text-sm">Created at</span>
              <span class="text-xs">{{ $moment(job.created_at).format('DD/MM/YYYY HH:mm') }}</span>
            </p>
          </div>

          <p class="text-3xl font-bold leading-none" v-if="job.job_type && hasPermission('job-type.view')">{{ job.job_type.name }}</p>
          <router-link
            v-if="job.contact && hasPermission('customer.view')"
            class="text-sm text-link inline-block"
            :to="'/customer/details/' + job.contact.id"
          >
            {{ job.contact.first_name }} {{ job.contact.last_name }}
            <span v-if="job.contact.company_name"> ({{ job.contact.company_name }})</span>
            <i class="fas fa-external-link-alt ml-1"></i>
          </router-link>

          <div class="flex flex-wrap items-end justify-between mt-4">
            <p class="leading-tight">
              <span class="block text-xs font-bold">Status:</span>
              <span
                class="inline-block text-white text-sm leading-normal px-2 font-bold"
                :class="job.status === 'completed' ? 'bg-green-500' : 'bg-orange-500'"
              >{{ job.status | sentenceCase }}</span>
            </p>
            <div v-if="hasPermission('job.edit')">
              <button
                v-if="job.status === 'ongoing'"
                type="button"
                class="btn btn-green text-sm font-bold leading-tight py-1"
                @click="setStatus('completed')">
                Complete job
              </button>
              <button
                v-else-if="job.status === 'completed'"
                type="button"
                class="btn btn-orange text-sm font-bold leading-tight py-1"
                @click="setStatus('ongoing')">
                Set ongoing
              </button>
            </div>
          </div>

          <div class="mt-4" v-if="job.note">
            <p>Note:</p>
            <div class="py-2 px-4 bg-theme-200 light:bg-theme-100 rounded">
              {{ job.note }}
            </div>
          </div>
        </div>

        <div class="box" v-if="hasAllPermissions(['customer.view', 'vehicle.view'])">
          <ul class="flex select-none mb-8">
            <li class="flex-1 mr-2">
              <a
                class="tab"
                :class="{ 'active': tabs.side.active === 'vehicle' }"
                @click="tabs.side.active = 'vehicle'"
                >
                Vehicle
              </a>
            </li>
            <li class="flex-1">
              <a
                class="tab"
                :class="{ 'active': tabs.side.active === 'contact' }"
                @click="tabs.side.active = 'contact'"
                >
                Customer
              </a>
            </li>
          </ul>
          <div v-if="tabs.side.active === 'vehicle'">
            <vehicle-information
              v-if="job.vehicle"
              :vehicle="job.vehicle"
              :edit="hasPermission('vehicle.edit')"
              :linkToVehicle="true"
              @updated="getJob"
            ></vehicle-information>
          </div>
          <div v-if="tabs.side.active === 'contact'">
            <contact-information
              v-if="job.contact"
              :contact="job.contact"
              contactType="customer"
              :edit="hasPermission('customer.edit')"
              :link-to-contact="true"
              @updated="getJob"
            ></contact-information>
          </div>
        </div>

      </div>
      <div class="w-full lg:w-2/3 xl:w-3/4 px-2">
        <div class="box">
          <ul class="flex select-none mb-8">
            <li class="flex-1 mr-2" v-if="hasPermission('invoice.view')">
              <a
                class="tab"
                :class="{ 'active': tabs.top.active === 'invoice' }"
                @click="tabs.top.active = 'invoice'"
                >
                Invoice
              </a>
            </li>
            <li class="flex-1 mr-2" v-if="hasPermission('job.edit')">
              <a
                class="tab"
                :class="{ 'active': tabs.top.active === 'remarks' }"
                @click="tabs.top.active = 'remarks'"
                >
                Remarks
              </a>
            </li>
            <li class="flex-1" v-if="hasPermission('vehicle.view')">
              <a
                class="tab"
                :class="{ 'active': tabs.top.active === 'job-history' }"
                @click="tabs.top.active = 'job-history'"
                >
                Job History
              </a>
            </li>
          </ul>
          <div v-if="job.invoices && tabs.top.active === 'invoice'">
            <div v-if="!job.invoices.length" class="text-center py-4">
              <p class="text-xl font-bold mb-2">No invoice for this job</p>
              <button
                v-if="hasPermission('invoice.create')"
                class="btn btn-purple py-2 px-3"
                @click="createInvoice"
                type="button"
              >
                <i class="fas fa-plus"></i>
                <span class="ml-2">Create Invoice</span>
              </button>
            </div>
            <div class="border border-purple p-1 -m-1" v-else>
              <create-invoice
                :invoiceID="job.invoices[0].id"
                :job="job"
                :contact-editable="false"
                @invoice-settled="refreshAndSetStatus('completed')"
              />
            </div>
          </div>
          <div v-if="tabs.top.active === 'remarks'">
            <div class="border border-purple p-1 -m-1">
              <job-remarks
                :job="job"
                @updated="setRemarks"
              />
            </div>
          </div>
          <div v-if="tabs.top.active === 'job-history'">
            <job-list
              v-if="job.vehicle"
              :vehicleID="job.vehicle.id"
              :hideJobID="[job.id]"
              :button-new="false"
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>


<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
const ContactInformation = resolve => require(['@/components/pages/ContactInformation'], resolve)
const VehicleInformation = resolve => require(['@/components/pages/VehicleInformation'], resolve)
const CreateInvoice = resolve => require(['@/components/pages/invoices/Edit'], resolve)
const JobRemarks = resolve => require(['@/components/pages/jobs/JobRemarksEdit'], resolve)
const JobList = resolve => require(['@/components/pages/jobs/List'], resolve)

export default {
  components: {
    ContactInformation,
    VehicleInformation,
    CreateInvoice,
    JobRemarks,
    JobList,
  },
  data() {
    return {
      jobID: this.$route.params.jobID,
      job: {},
      prints: {
        remarks: null,
      },
      tabs: {
        side: {
          active: 'vehicle',
        },
        top: {
          active: 'invoice',
        },
      },
    }
  },
  computed: {
    ...mapGetters({
      Countries: 'countries/countries',
      CountryByID: 'countries/countryByID',
      config: 'config/config',
      hasPermission: 'user/hasPermission',
      hasAllPermissions: 'user/hasAllPermissions',
    })
  },
  mounted() {
    this.getJob()
  },
  methods: {
    refreshAndSetStatus(status) {
      this.getJob()
        .then(this.setStatus(status))
    },
    setStatus(status) {
      if (this.job.status === status) {
        return
      }
      this.$swal.fire({
        title: 'Confirmation',
        text: 'Set job "' + status + '"?',
        type: 'question',
        confirmButtonText: 'Yes',
        showCancelButton: true,
        cancelButtonText: 'No',
      })
        .then((result) => {
          if (result.value) {
            this.job.status = status
            this.updateJob()
          }
        })
    },
    setRemarks(remarks) {
      this.job.remarks = remarks
      this.updateJob()
    },
    updateJob() {
      return axios.put('jobs/' + this.jobID, this.job)
    },
    getJob() {
      return new Promise((resolve, reject) => {
        axios.get('jobs/' + this.jobID)
          .then((response) => {
            const job = response.data

            this.job = Object.assign({}, job)
            resolve()
          })
          .catch((error) => {
            console.log(error.response)
            reject(error)
            this.$swal.fire({
              title: 'Job not found',
              type: 'warning',
            })
              .then(() => {
                // this.$router.push('/job/list')
              })
          })
      })
    },
    createInvoice() {
      this.job.invoices.push({
        contact_id: this.job.contact.id,
        type: 'sales',
        created_at: this.$moment().format(this.config.dateTimeFormats.api),
      })
      this.updateJob().then(this.getJob)
    },
  }
}
</script>
